import { useState, type ChangeEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon.tsx';
import { comparisonApi, type PoUpload, type PoDocKind } from '../features/comparison/api.ts';
import { downloadPdf } from '../features/documents/api.ts';
import { fileToBase64, UPLOAD_ACCEPT } from '../lib/upload.ts';
import { t } from '../lib/strings.ts';

/**
 * Dialog opened from "މުދާ ލިބިއްޖެ" (goods received). Collects the required
 * Purchase Order(s) plus optional other supporting documents, lets the
 * officer preview the merged package, then marks goods received — which
 * mints a vendor invoice-confirm token and SMSes the vendor.
 */
export function GoodsReceivedDialog({
  requestId,
  open,
  onOpenChange,
}: {
  requestId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const { data: docs } = useQuery({
    queryKey: ['poUploads', requestId],
    queryFn: () => comparisonApi.listPoUploads(requestId),
    enabled: open,
  });
  const docList = docs ?? [];
  const pos = docList.filter((d) => d.kind === 'PO');
  const others = docList.filter((d) => d.kind === 'OTHER');

  const uploadMutation = useMutation({
    mutationFn: async ({ kind, file }: { kind: PoDocKind; file: File }) => {
      const { dataBase64, mime } = await fileToBase64(file);
      return comparisonApi.uploadPo(requestId, { kind, fileName: file.name, mime, dataBase64 });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['poUploads', requestId] }),
    onError: () => setErr(t.requestDetail.poInvalidFile),
  });

  const deleteMutation = useMutation({
    mutationFn: (poId: string) => comparisonApi.deletePo(requestId, poId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['poUploads', requestId] }),
    onError: () => setErr(t.requestDetail.poDeleteFailed),
  });

  const sendMutation = useMutation({
    mutationFn: () => comparisonApi.goodsReceived(requestId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comparison', requestId] });
      void qc.invalidateQueries({ queryKey: ['request', requestId] });
      onOpenChange(false);
    },
    onError: () => setErr(t.requestDetail.goodsReceivedFailed),
  });

  function onPick(kind: PoDocKind) {
    return async (e: ChangeEvent<HTMLInputElement>) => {
      setErr(null);
      const files = Array.from(e.target.files ?? []);
      e.target.value = '';
      for (const file of files) await uploadMutation.mutateAsync({ kind, file }).catch(() => undefined);
    };
  }

  async function handlePreview() {
    setPreviewing(true);
    try { await downloadPdf(comparisonApi.paymentPackageUrl(requestId), `liyekunthah-${requestId}.pdf`); }
    finally { setPreviewing(false); }
  }

  const canSend = pos.length >= 1 && !sendMutation.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out" />
        <Dialog.Content className="card-paper fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 space-y-4 overflow-y-auto p-5 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95">
          <div className="space-y-1">
            <Dialog.Title className="font-serif text-lg text-foreground">{t.requestDetail.goodsReceivedTitle}</Dialog.Title>
            <Dialog.Description className="text-sm text-muted-foreground">{t.requestDetail.sendDialogIntro}</Dialog.Description>
          </div>

          <DocBox
            label={t.requestDetail.poSectionLabel}
            docs={pos}
            uploading={uploadMutation.isPending}
            emptyHint={t.requestDetail.poRequiredHint}
            onPick={onPick('PO')}
            onRemove={(id) => deleteMutation.mutate(id)}
            removing={deleteMutation.isPending}
          />
          <DocBox
            label={t.requestDetail.otherDocsLabel}
            docs={others}
            uploading={uploadMutation.isPending}
            onPick={onPick('OTHER')}
            onRemove={(id) => deleteMutation.mutate(id)}
            removing={deleteMutation.isPending}
          />

          {err && <p className="text-xs text-destructive">{err}</p>}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <button type="button" onClick={() => void handlePreview()} disabled={previewing} className="btn-secondary btn-sm gap-1">
              <Icon name="visibility" size={14} />
              {previewing ? t.requestDetail.previewLoading : t.requestDetail.previewLiyekunthah}
            </button>
            <div className="flex items-center gap-2">
              <button type="button" className="btn-ghost btn-sm" onClick={() => onOpenChange(false)}>{t.common.cancel}</button>
              <button type="button" className="btn-primary btn-sm" disabled={!canSend} onClick={() => sendMutation.mutate()}>
                {sendMutation.isPending ? t.requestDetail.sendingToFinance : t.requestDetail.goodsReceivedConfirm}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DocBox({
  label,
  docs,
  uploading,
  emptyHint,
  onPick,
  onRemove,
  removing,
}: {
  label: string;
  docs: PoUpload[];
  uploading: boolean;
  emptyHint?: string;
  onPick: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemove: (id: string) => void;
  removing: boolean;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="status-label text-muted-foreground">{label}</span>
        <label className="btn-secondary btn-sm cursor-pointer gap-1">
          <Icon name="upload" size={14} />
          {uploading ? t.requestDetail.poUploading : t.requestDetail.poAddBtn}
          <input type="file" multiple accept={UPLOAD_ACCEPT} className="hidden" onChange={(e) => void onPick(e)} disabled={uploading} />
        </label>
      </div>
      {docs.length > 0 ? (
        <ul className="mt-2 divide-y divide-border text-sm">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-2 py-1">
              <span className="flex min-w-0 items-center gap-1.5">
                <Icon name="description" size={14} className="shrink-0 text-muted-foreground" />
                <span className="truncate">{d.fileName}</span>
              </span>
              <button type="button" onClick={() => onRemove(d.id)} disabled={removing} className="btn-ghost btn-sm text-destructive" aria-label={t.requestDetail.poRemove}>
                <Icon name="close" size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : emptyHint ? (
        <p className="mt-2 text-xs text-muted-foreground">{emptyHint}</p>
      ) : null}
    </div>
  );
}
